/* eslint-disable deprecation/deprecation */
import React from 'react'
import styled from 'styled-components'
import { Skeleton } from '@mui/material'
import { BreakpointAsNumbers, Breakpoint, CardGrid, TypeformPopup, Button, Loader } from '@tumelo/shared'
import { Column, Grid } from '@tumelo/designsystem'
import { useWindowWidth } from '@tumelo/shared/utils'
import { useAppSelector } from '../../../application/store'
import { TopHoldingsContainer } from '../../TopHoldings'
import { PageHeader } from '../../PageHeader'
import { DashboardVotesOpenContainer } from '../../DashboardVotes'
import { ReturningCardsContainer } from '../../ReturningCards/ReturningCardsContainer'
import { isIntermediaryState, isValueState, Payload } from '../../../application/payload'
import { useHabitatVotingPolicies, useVotingPolicySelection } from '../../../application/features/votingPolicies/hooks'
import { useAccount } from '../../../application/features/account/hooks'
import { VotingPoliciesBanner } from '../../VotingPoliciesBanner'
import { useInvestor } from '../../../application/features/investor/useInvestor'
import { selectSoftConfig } from '../../../application/features/config/selectors'
import { VotePoliciesModal } from '../../VotingPoliciesModal'

interface Props {
  handleFundClick: () => void
  handleCompanyClick: (id: string) => void
  handleOpenVotes: () => void
  name: string | null
}

export const Dashboard: React.VFC<Props> = ({ handleFundClick, handleCompanyClick, handleOpenVotes, name }) => {
  const currentWindowSize = useWindowWidth()
  const [isMobile, setIsMobile] = React.useState<boolean>(false)
  const typeformUrl = 'https://form.typeform.com/to/fC7WAcWH'
  const { habitat, investorId, votingPoliciesEnabled } = useAppSelector((state) => ({
    habitat: state.config.config.habitat,
    investorId: Payload.toMaybe(state.investor.investor)?.investorId,
    votingPoliciesEnabled: selectSoftConfig(state).featureSwitches.enableVotingPolicies,
  }))
  const { investor } = useInvestor()
  const account = useAccount(investor)
  const votingPolicySelection = useVotingPolicySelection(account)
  const habitatVotingPolicies = useHabitatVotingPolicies()

  const habitatVotingPoliciesFetched =
    isValueState(habitatVotingPolicies) && habitatVotingPolicies !== undefined && habitatVotingPolicies.length > 0
  React.useEffect(() => {
    setIsMobile(BreakpointAsNumbers.mobile > currentWindowSize)
  }, [currentWindowSize])

  if (isIntermediaryState(habitatVotingPolicies)) {
    return (
      <StyledDiv>
        <Loader />
      </StyledDiv>
    )
  }

  return (
    <>
      <PageHeader
        title={<span data-hj-suppress>Hello {name != null ? name : <StyledSkeleton />}</span>}
        callToAction={
          !isMobile ? (
            <TypeformPopup url={typeformUrl} hiddenFields={investorId ? { habitat, user: investorId } : { habitat }}>
              <Container>
                <Button inverse size="medium">
                  Tell us what you think
                </Button>
              </Container>
            </TypeformPopup>
          ) : undefined
        }
      />
      <StyledGrid>
        <Column xs={12} sm={12} md={12} lg={12}>
          {votingPoliciesEnabled && habitatVotingPoliciesFetched && (
            <VotePoliciesModal votingPolicies={habitatVotingPolicies} />
          )}
          {votingPoliciesEnabled &&
            habitatVotingPoliciesFetched &&
            (!isValueState(votingPolicySelection) || votingPolicySelection === undefined) && <VotingPoliciesBanner />}
        </Column>
        <Column xs={12} sm={12} md={6} lg={6}>
          <TopHoldingsContainer onClickCta={handleFundClick} onCompanyClick={handleCompanyClick} />
        </Column>
        <Column xs={12} sm={12} md={6} lg={6}>
          <DashboardVotesOpenContainer handleCallToAction={handleOpenVotes} />
        </Column>
      </StyledGrid>
      <CardGrid>
        <ReturningCardsContainer />
      </CardGrid>

      {isMobile && (
        <TypeformPopup url={typeformUrl} hiddenFields={investorId ? { habitat, investorId } : { habitat }}>
          <MobileTypeformContainer>
            <Button inverse size="medium" fullWidth>
              Tell us what you think
            </Button>
          </MobileTypeformContainer>
        </TypeformPopup>
      )}
    </>
  )
}

const StyledSkeleton = styled(Skeleton)`
  display: inline-block !important;
  width: 6rem;
`

const MobileTypeformContainer = styled.div`
  padding-top: 2rem;
`

const Container = styled.div`
  display: flex;
  max-width: 460px;
  justify-content: flex-end;

  @media (max-width: ${Breakpoint.tablet}) {
    justify-content: flex-start;
  }
`
const StyledGrid = styled(Grid)`
  height: auto;
`

const StyledDiv = styled.div`
  height: 100vh;
  max-height: 100vh;
`
